import { useCallback, useEffect, useState } from "react";
import Lenis from "@studio-freight/lenis";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import AgeGate from "@/components/noor/AgeGate";
import Nav from "@/components/noor/Nav";
import EditionCounter from "@/components/noor/EditionCounter";
import Plausible from "@/components/noor/Plausible";
import Hero from "@/components/noor/Hero";
import Heritage from "@/components/noor/Heritage";
import Craft from "@/components/noor/Craft";
import Tasting from "@/components/noor/Tasting";
import Gallery from "@/components/noor/Gallery";
import Reserve from "@/components/noor/Reserve";
import Contact from "@/components/noor/Contact";
import Footer from "@/components/noor/Footer";
import InnerCircle from "@/pages/InnerCircle";
import Manifesto from "@/pages/Manifesto";
import Admin from "@/pages/Admin";
import Cocktails from "@/pages/Cocktails";
import Stockists from "@/pages/Stockists";
import ComingSoon from "@/pages/ComingSoon";
import ChapterRail from "@/components/noor/ChapterRail";
import CursorCompanion from "@/components/noor/CursorCompanion";
import SkipIntroButton from "@/components/noor/SkipIntroButton";
import { track } from "@/lib/analytics";

// Returning-visitor key. When present in localStorage, the hero deconstruction
// is bypassed and the photographic bottle renders immediately.
const VISITED_KEY = "noor_visited";
const INTRO_DURATION_MS = 3300;

function readVisited() {
  try { return localStorage.getItem(VISITED_KEY) === "true"; }
  catch { return false; }  // Safari private mode etc. — fail open.
}

function writeVisited() {
  try { localStorage.setItem(VISITED_KEY, "true"); }
  catch {
    // Safari private mode / quota exceeded — non-fatal; visitor will simply
    // see the intro again on their next session.
  }
}

// When REACT_APP_COMING_SOON_MODE === "true" the root `/` shows the countdown landing.
// Append `?preview=full` to bypass and view the full launch experience without flipping
// the env var. All other routes (manifesto, inner-circle, cocktails, stockists, admin)
// remain reachable by direct URL regardless of this flag.
const COMING_SOON_MODE = process.env.REACT_APP_COMING_SOON_MODE === "true";

function isPreviewBypass() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("preview") === "full";
}

function useLenis() {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return undefined;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let frame = 0;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
    // Mount-only effect: Lenis is a module import and `frame` is intentionally
    // local mutable state, so the empty dependency array is correct.
  }, []);
}

function Home() {
  useLenis();
  // Returning visitors (localStorage flag) skip straight to the bottle photo.
  // First-time visitors see the deconstruction; the flag is written when the
  // 3.3s sequence completes naturally OR when they tap "Skip intro".
  const [isSkipped, setIsSkipped] = useState(readVisited);

  useEffect(() => {
    if (isSkipped) return undefined;
    const t = setTimeout(() => {
      writeVisited();
      track("intro_status", { type: "completed" });
    }, INTRO_DURATION_MS);
    return () => clearTimeout(t);
  }, [isSkipped]);

  const handleSkip = useCallback(() => {
    writeVisited();
    track("intro_status", { type: "skipped" });
    setIsSkipped(true);
  }, []);

  return (
    <>
      <AnimatePresence>
        {!isSkipped && <SkipIntroButton key="skip" onClick={handleSkip} />}
      </AnimatePresence>
      <Hero isSkipped={isSkipped} />
      <Heritage />
      <Craft />
      <Tasting />
      <Gallery />
      <Reserve />
      <Contact />
    </>
  );
}

function Shell({ children }) {
  return (
    <>
      <Plausible />
      <AgeGate />
      <CursorCompanion />
      <div className="grain-overlay" />
      <EditionCounter />
      <Nav />
      <ChapterRail />
      {children}
      <Footer />
    </>
  );
}

function AdminShell({ children }) {
  // Bare shell for /admin — no age gate, no public nav, no chapter rail.
  // Just the grain overlay + cursor companion so it still feels NOOR.
  return (
    <>
      <CursorCompanion />
      <div className="grain-overlay" />
      {children}
    </>
  );
}

function ComingSoonShell({ children }) {
  // Minimal shell for the pre-launch landing — AgeGate runs first per legal
  // requirement, Plausible still fires so we capture pre-launch interest, the
  // grain overlay keeps the texture. No public nav, no edition counter, no
  // chapter rail — those belong to the full launch site.
  return (
    <>
      <Plausible />
      <AgeGate />
      <CursorCompanion />
      {children}
    </>
  );
}

function RootRoute() {
  const showComingSoon = COMING_SOON_MODE && !isPreviewBypass();
  if (showComingSoon) {
    return (
      <ComingSoonShell>
        <ComingSoon />
      </ComingSoonShell>
    );
  }
  return (
    <Shell>
      <main>
        <Home />
      </main>
    </Shell>
  );
}

// Public-content routes share a common Shell wrapper. Listing them as data
// lets `<App />` stay flat & declarative.
const PUBLIC_ROUTES = [
  { path: "/manifesto",    element: <Manifesto /> },
  { path: "/inner-circle", element: <InnerCircle /> },
  // Pre-launch routes — pages exist but are intentionally absent from the
  // public nav until launch. Direct-URL access only.
  { path: "/cocktails",    element: <Cocktails /> },
  { path: "/stockists",    element: <Stockists /> },
];

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route
        path="/admin"
        element={
          <AdminShell>
            <Admin />
          </AdminShell>
        }
      />
      {PUBLIC_ROUTES.map(({ path, element }) => (
        <Route key={path} path={path} element={<Shell>{element}</Shell>} />
      ))}
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}
